import React, { FC, useCallback, useEffect, useState } from "react";
import { styled } from "@mui/system";
import { COLORS, GAPS, SPACINGS } from "../../common/consts";
import { SettingsCaption, SettingsControlField, SettingsFooter, SettingsGroup, SettingsHeader, SettingsPanel, SettingsPanelSkeleton, SettingsRow } from "../Settings";
import Button, { ButtonVariants } from "../Display/Button";
import Toggle from "../DataInput/Toggle";
import Select from "../DataInput/Select";
import Input from "../DataInput/Input";
import { getPhoneEnrichmentSettings, getProfileEnrichmentSettings, updatePhoneEnrichmentSettings, updateProfileEnrichmentSettings } from "../../services/firebase";
import {
  PHONE_ENRICHMENT_RULE_HELP,
  PHONE_ENRICHMENT_RULE_LABELS,
  PhoneEnrichmentRule,
  PhoneEnrichmentRuleKind,
  PhoneEnrichmentSettings,
} from "../../types/addons";

const RuleRow = styled("div")`
  display: grid;
  grid-template-columns: auto minmax(220px, 1fr) minmax(200px, 1.4fr) auto;
  gap: ${GAPS.md};
  align-items: center;
  padding: ${SPACINGS.md} 0;
  border-top: 1px solid var(--color-backgroundBrand, ${COLORS.backgroundBrand});

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const KINDS: PhoneEnrichmentRuleKind[] = ["has_open_call_task", "in_sequence_with_call_step", "in_any_active_sequence", "job_title_contains", "owner_email_in"];
const TAKES_VALUES: PhoneEnrichmentRuleKind[] = ["job_title_contains", "owner_email_in"];
const PLACEHOLDER: Partial<Record<PhoneEnrichmentRuleKind, string>> = {
  job_title_contains: "vp, head of, director",
  owner_email_in: "rep@company.com, rep2@company.com",
};

const newId = () => `rule_${Math.random().toString(36).slice(2, 10)}`;

/**
 * When boring buys phone numbers. Lookups are billed per use, so admins pick
 * automatic or manual, which line types count, and rules that narrow
 * automatic spend to prospects someone is about to call.
 */
const PhoneEnrichmentSection: FC = () => {
  const [settings, setSettings] = useState<PhoneEnrichmentSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [valueDrafts, setValueDrafts] = useState<Record<string, string>>({});
  const [standaloneProfile, setStandaloneProfile] = useState<boolean | null>(null);

  const load = useCallback(async () => {
    try {
      setSettings(await getPhoneEnrichmentSettings());
      setStandaloneProfile((await getProfileEnrichmentSettings()).standaloneLookup);
    } catch (e: any) {
      setMessage(e?.message || "Could not load phone enrichment settings.");
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  if (!settings && message) return <SettingsCaption>{message}</SettingsCaption>;

  /* The enrichment panel's own shape: heading, the three switches, the
     numbers field, and the rules row with its Add rule button. */
  if (!settings)
    return (
      <SettingsPanelSkeleton
        titleWidth={128}
        captionWidths={["96%", "64%"]}
        rows={[
          { labelWidth: 240, captionWidth: "72%", control: "toggle" },
          { labelWidth: 420, captionWidth: "88%", control: "toggle" },
          { labelWidth: 320, captionWidth: "76%", control: "toggle" },
          { labelWidth: 160, control: "field" },
          { labelWidth: 220, captionWidth: "80%", control: "button" },
        ]}
      />
    );

  const patch = (next: Partial<PhoneEnrichmentSettings>) => {
    setSettings({ ...settings, ...next });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const rules = settings.rules.map((r) =>
        TAKES_VALUES.includes(r.kind) && valueDrafts[r.id] !== undefined
          ? { ...r, values: valueDrafts[r.id].split(",").map((v) => v.trim()).filter(Boolean) }
          : r
      );
      const saved = await updatePhoneEnrichmentSettings({ ...settings, rules });
      setSettings(saved);
      setValueDrafts({});
      setDirty(false);
      setMessage("Saved.");
    } catch (e: any) {
      setMessage(e?.message || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const updateRule = (id: string, next: Partial<PhoneEnrichmentRule>) => patch({ rules: settings.rules.map((r) => (r.id === id ? { ...r, ...next } : r)) });
  const removeRule = (id: string) => patch({ rules: settings.rules.filter((r) => r.id !== id) });
  const addRule = () => patch({ rules: [...settings.rules, { id: newId(), kind: "has_open_call_task", enabled: true }] });

  return (
    <SettingsPanel>
      <SettingsHeader
        title="Enrichment"
        caption="Every lookup is billed, so decide when boring buys a number. Reps can always press Find number on a prospect when manual lookups are on."
      />

      <SettingsGroup>
        <SettingsRow
          label="Buy numbers automatically"
          caption="Runs when a prospect goes through the pipeline. Rules below narrow who qualifies."
          control={<Toggle checked={settings.autoEnrich} onChange={(v) => patch({ autoEnrich: v })} />}
        />
        <SettingsRow
          label="Look up a profile even when the email came from a pattern"
          caption="Company and title normally arrive alongside an email lookup. This also looks them up for prospects whose email was solved without one, an extra vendor call each."
          control={
            <Toggle
              checked={!!standaloneProfile}
              onChange={async (v) => {
                setStandaloneProfile(v);
                try {
                  await updateProfileEnrichmentSettings(v);
                } catch {
                  setStandaloneProfile(!v);
                  setMessage("Could not save that.");
                }
              }}
            />
          }
        />
        <SettingsRow
          label="Allow manual lookups from the extension"
          caption="Shows the Find number button on prospect pages for reps with a phone enrichment seat."
          control={<Toggle checked={settings.manualEnabled} onChange={(v) => patch({ manualEnabled: v })} />}
        />
        <SettingsRow
          label="Numbers to keep"
          control={
            <SettingsControlField>
              <Select
                value={settings.numberScope}
                onChange={(e: any) => patch({ numberScope: e?.target?.value || e })}
                options={[
                  { value: "mobile", label: "Mobiles only" },
                  { value: "mobile_or_direct", label: "Mobiles and direct lines" },
                  { value: "all", label: "Any verified number" },
                ]}
              />
            </SettingsControlField>
          }
        />
        <div>
          <SettingsRow
            label="Automatic lookup rules"
            caption="A prospect qualifies when any rule matches. With no rules, every prospect in the pipeline qualifies."
            control={
              <Button variant={ButtonVariants.light} type="button" onClick={addRule} disabled={!settings.autoEnrich}>
                Add rule
              </Button>
            }
          />
          {settings.rules.map((rule) => (
            <RuleRow key={rule.id}>
              <Toggle checked={rule.enabled} onChange={(v) => updateRule(rule.id, { enabled: v })} />
              <Select
                value={rule.kind}
                onChange={(e: any) => updateRule(rule.id, { kind: e?.target?.value || e, values: undefined })}
                options={KINDS.map((k) => ({ value: k, label: PHONE_ENRICHMENT_RULE_LABELS[k] }))}
              />
              <div>
                {TAKES_VALUES.includes(rule.kind) ? (
                  <Input
                    fullWidth
                    placeholder={PLACEHOLDER[rule.kind]}
                    value={valueDrafts[rule.id] ?? (rule.values || []).join(", ")}
                    onChange={(e: any) => {
                      setValueDrafts({ ...valueDrafts, [rule.id]: e.target.value });
                      setDirty(true);
                    }}
                  />
                ) : (
                  <SettingsCaption>{PHONE_ENRICHMENT_RULE_HELP[rule.kind]}</SettingsCaption>
                )}
              </div>
              <Button variant={ButtonVariants.text} type="button" onClick={() => removeRule(rule.id)}>
                Remove
              </Button>
            </RuleRow>
          ))}
        </div>
      </SettingsGroup>

      <SettingsFooter start={message && <SettingsCaption>{message}</SettingsCaption>}>
        <Button variant={ButtonVariants.brand} type="button" disabled={!dirty || saving} loading={saving} onClick={save}>
          Save
        </Button>
      </SettingsFooter>
    </SettingsPanel>
  );
};

export default PhoneEnrichmentSection;
